import React from 'react';
import { Alert, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from '../../i18n';
import type { BulkDownloadStatus } from './bulkDownload';

interface BulkDownloadStatusLineProps {
  status: BulkDownloadStatus | null;
  onDismiss: () => void;
}

/**
 * The progress count of a "Download (N files)" run, and its outcome once done.
 *
 * Stays on screen until dismissed when a file failed: the names of what did not
 * start are the one thing the user cannot reconstruct from the browser's own
 * download list.
 */
const BulkDownloadStatusLine: React.FC<BulkDownloadStatusLineProps> = ({ status, onDismiss }) => {
  const { t } = useTranslation();
  if (!status) return null;

  if (status.running) {
    return (
      <Alert severity="info" sx={{ mb: 2 }} data-testid="bulk-download-status">
        <Typography variant="body2">
          {t('fileManagement.bulkDownloadProgress', { done: status.done, total: status.total })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={status.total ? (status.done / status.total) * 100 : 0}
          sx={{ my: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary">
          {t('fileManagement.bulkDownloadBrowserHint')}
        </Typography>
      </Alert>
    );
  }

  const failed = status.failedNames.length;
  return (
    <Alert
      severity={failed ? 'warning' : 'success'}
      onClose={onDismiss}
      sx={{ mb: 2 }}
      data-testid="bulk-download-status"
    >
      {failed
        ? t('fileManagement.bulkDownloadPartial', {
            started: status.started,
            total: status.total,
            names: status.failedNames.join(', '),
          })
        : t('fileManagement.bulkDownloadDone', { count: status.started })}
    </Alert>
  );
};

export default BulkDownloadStatusLine;
